import { FormattedMessage } from "react-intl";

import BlockAccordion from "@components/accordions/BlockAccordion";
import ActionIconButton from "@components/buttons/ActionIconButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import DateStamp from "@components/dates/DateStamp";
import styles from "@components/forms/careProfile/InternalNotes.module.css";
import Nbsp from "@components/Nbsp";
import BodyCopy from "@components/typography/BodyCopy";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { GetJourneyWithExpertPlanQuery, InternalNoteKindEnum } from "@typing/Generated";

type Note = NonNullable<GetJourneyWithExpertPlanQuery["journey"]["internalNotes"]>[0];

type InternalNoteProps = {
  handleDelete: () => void;
  handleEdit: () => void;
  journeyId: string;
  note: Note;
};

const InternalNote = ({ handleDelete, handleEdit, journeyId, note }: InternalNoteProps) => {
  const hasBeenUpdated = Math.abs(note.createdAt - note.updatedAt) >= 1000;

  const sanitizedBody = useSanitizeHtml(note.body, journeyId, note);

  return (
    <BlockAccordion
      color="white"
      content={
        <>
          <BodyCopy bold>{sanitizedBody}</BodyCopy>
          {note.kind === InternalNoteKindEnum.NOTE && (
            <div className={styles.actions}>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.delete"
                icon="trash"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete();
                }}
              />
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.edit"
                icon="edit"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEdit();
                }}
              />
            </div>
          )}
        </>
      }
      heading={
        <>
          <BodyCopy bold color="primary-500">
            {note.title ? note.title : <FormattedMessage id={`pages.carePlan.base.expert.versionTitle.${note.kind}`} />}
          </BodyCopy>
          <span className="ion-float-right">
            <BodyCopy bold color="gray">
              <DateAndTimeStamp value={note.createdAt} />
            </BodyCopy>
            {hasBeenUpdated && (
              <>
                <Nbsp />
                <BodyCopy className={styles.edited} italic>
                  <FormattedMessage
                    id="pages.carePlan.base.expert.edited"
                    values={{ date: <DateStamp showWeekday={false} value={note.updatedAt} /> }}
                  />
                </BodyCopy>
              </>
            )}
          </span>
        </>
      }
    />
  );
};

export default InternalNote;
